import { useAuth0 } from "@/core/auth0";
import { PermissionKey } from "@/core/permissions";
import {
  PermissionEntitlements,
  useFMPermissionEntitlements,
} from "@/core/splitio";

// Product activation and campaign management have no partner-level switch:
// every partner with the section has the capability, and who holds it is the
// permission alone.
const ENTITLED: Record<PermissionKey, (e: PermissionEntitlements) => boolean> =
  {
    "orders.create": (e) => e.createOrders,
    "orders.cancel": (e) => e.cancelOrders,
    "orders.decide_requests": (e) => e.cancelOrders,
    "catalog.manage": (e) => e.manageCatalog,
    "catalog.activate_products": () => true,
    "campaigns.manage": () => true,
  };

/**
 * Whether the signed-in user holds the permission. Owners and Flexspace staff
 * hold every permission; admins and standard users hold what was granted. A profile without a permission list (from before the
 * field existed) counts as fully granted — the API is the boundary, this only
 * decides what to draw. Pair it with the capability's own partner-level flag
 * where one exists; `useCan` does that for the common case.
 */
export function useHasPermission(permission: PermissionKey): boolean {
  const { userProfile } = useAuth0();
  if (!userProfile) return false;
  if (userProfile.isSuperAdmin || userProfile.role === "owner") return true;
  return (
    userProfile.permissions === undefined ||
    userProfile.permissions.includes(permission)
  );
}

/**
 * The one door for "may the signed-in user do this": the partner must be
 * entitled to the capability and the user must hold the permission.
 */
export function useCan(permission: PermissionKey): boolean {
  const { userProfile } = useAuth0();
  const hasPermission = useHasPermission(permission);
  const entitlements = useFMPermissionEntitlements(
    userProfile?.partnerId ?? "",
    userProfile?.auth0UserId ?? ""
  );
  return ENTITLED[permission](entitlements) && hasPermission;
}
